// File: frontend/src/context/BrandingContext.js
// Version: 1.1 (Phase 9 — features из /branding, аддитивно)
// Description: Single source of truth for tenant identity on the frontend.
// Replaces 3 duplicate fetch('/api/v1/branding') calls (Login.js:19, Home.js:37, Navbar.js:89).
// Owns the html.fitenko class toggle and conditional Fontsource CSS loading.

import React, { createContext, useContext, useEffect, useState } from 'react';

// 🔴 УМОЛЧАНИЕ = АРЕНДАТОР ЭТОГО РЕПОЗИТОРИЯ, А НЕ ЧУЖОЙ. fitenko-os выкатывается только как
//    Fitenko (решение 01.10.2026). Прежнее умолчание 'mlg' 13.10.2026 в 23:07 показало владельцу
//    интерфейс MLG OS: бэкенд перезапускался при выкатке, /branding отдал 502 три раза, ошибка
//    глоталась, и чужое оформление держалось до перезагрузки страницы. Отказ ответа ≠ ответ «я MLG».
const DEFAULT_BRANDING = { instance_name: 'fitenko', company_name: 'Fitenko Law PLLC' };

// Повторы при сбое: окно рестарта бэкенда при выкатке ~10 с; сумма пауз ~30 с перекрывает его.
export const BRANDING_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];

// Ответ годен, только если это объект с непустым instance_name: тело 502/500 (HTML или {detail})
// не должно подменить арендатора.
const godenOtvet = (data) => !!data && typeof data === 'object' && typeof data.instance_name === 'string' && data.instance_name.trim() !== '';

// Phase 9: признаки арендатора из /api/v1/branding (`features`). Нет поля / не объект → ПУСТО
// (у Fitenko/MLG до и после выкатки 09-02 — `{}`). Замороженный синглтон: ссылка стабильна
// для зависимостей эффектов.
const PUSTO_PRIZNAKOV = Object.freeze({});

const BrandingContext = createContext({
    branding: DEFAULT_BRANDING,
    isFitenko: false,
    loading: true,
    features: PUSTO_PRIZNAKOV,
    featuresError: null,
});

export const useBranding = () => useContext(BrandingContext);

// Module-scoped cache mirrors Navbar.js:11 `_brandingCache` pattern.
let _cache = null;

export const BrandingProvider = ({ children }) => {
    const [branding, setBranding] = useState(_cache || DEFAULT_BRANDING);
    const [loading, setLoading] = useState(!_cache);

    useEffect(() => {
        if (_cache) return undefined;
        let otmeneno = false;
        let tajmer = null;
        const popytka = (nomer) => {
            fetch('/api/v1/branding')
                .then((r) => {
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    return r.json();
                })
                .then((data) => {
                    if (!godenOtvet(data)) throw new Error('branding: нет instance_name');
                    if (otmeneno) return;
                    _cache = data;
                    setBranding(data);
                    setLoading(false);
                    // Persist for next-load FOUC prevention (inline <script> in index.html reads this).
                    try { localStorage.setItem('lastTenant', data.instance_name); } catch (e) { /* localStorage disabled */ }
                })
                .catch(() => {
                    if (otmeneno) return;
                    if (nomer < BRANDING_RETRY_DELAYS_MS.length) {
                        tajmer = setTimeout(() => popytka(nomer + 1), BRANDING_RETRY_DELAYS_MS[nomer]);
                    } else {
                        setLoading(false); // остаётся умолчание этого репозитория — fitenko
                    }
                });
        };
        popytka(0);
        return () => { otmeneno = true; if (tajmer) clearTimeout(tajmer); };
    }, []);

    const isFitenko = branding.instance_name === 'fitenko';
    const features = (branding && branding.features && typeof branding.features === 'object' && !Array.isArray(branding.features)) ? branding.features : PUSTO_PRIZNAKOV;
    const featuresError = (branding && branding.features_error) || null;

    // Idempotent class toggle (inline <script> in index.html may have already set it).
    useEffect(() => {
        const html = document.documentElement;
        if (isFitenko) html.classList.add('fitenko');
        else html.classList.remove('fitenko');
    }, [isFitenko]);

    // Conditional font CSS — Webpack code-splits these into their own chunk.
    // Гарнитуры §3.1 макета: Manrope (интерфейс) и DM Serif Display (заголовки).
    // MLG bundle truly does not contain these .woff2 — импорт живёт под рубежом.
    useEffect(() => {
        if (!isFitenko) return;
        Promise.all([
            import('@fontsource/manrope/400.css'),
            import('@fontsource/manrope/500.css'),
            import('@fontsource/manrope/600.css'),
            import('@fontsource/manrope/700.css'),
            import('@fontsource/dm-serif-display/400.css'),
        ]).catch(() => { /* font load failure non-fatal — system fallback used */ });
    }, [isFitenko]);

    return (
        <BrandingContext.Provider value={{ branding, isFitenko, loading, features, featuresError }}>
            {children}
        </BrandingContext.Provider>
    );
};
